// Description parser — ported from casi-mobile/lib/cases/description_parser.dart
//
// Parses raw ticket description text into structured sections.
// Ticket descriptions from the Cyflare ONE API are long plain-text blobs
// with consistent section headings (e.g. "Executive Summary", "Key Details").

import { DescriptionSection, ParsedDescription } from '../models/ticket.models';

// Section header patterns to search for, in priority order.
const SECTION_PATTERNS: { title: string; pattern: RegExp }[] = [
  { title: 'Executive Summary', pattern: /Executive Summary(?:\s+AI-Assisted)?/ },
  { title: 'SOC Response Actions', pattern: /SOC Response Actions(?:\s*-\s*Use Case\s*#?\d+)?(?:\s*:)?/ },
  { title: 'Recommended Remediation', pattern: /Recommended Remediation/ },
  { title: 'Remediation Actions', pattern: /Remediation Actions\s*\??:?/ },
  { title: 'Key Details', pattern: /Key Details:?/ },
  { title: 'Threat Details', pattern: /Threat Details:?/ },
  { title: 'Endpoint Details', pattern: /Endpoint Details:?/ },
  { title: 'SOC Findings', pattern: /SOC Findings:?/ },
  { title: 'Threat Intelligence', pattern: /Threat Intelligence & Reputation/ },
  { title: 'Indicators of Compromise', pattern: /Indicators of Compromise/ },
  { title: 'Network Connections', pattern: /Network Connections/ },
  { title: 'Processes Involved', pattern: /Process(?:es)? Involved/ },
  { title: 'Cross-Service Intelligence', pattern: /(?:Cross-Service Intelligence|Gluebooks Enrichment)/ },
  { title: 'Alfie Insights', pattern: /Alfie Insights\s*\(Beta\)/ },
  { title: 'SOC Recommended Actions', pattern: /SOC Recommended Actions/ },
  /**
   * Boundaries added from a survey of 97,024 production SOC Response Actions
   * sections. Each of these follows the actions in real tickets, and without
   * them the section ran on and swallowed the next block whole:
   *
   *   "Alfie AI Summarization (Beta)"  — ~4,500 sections. The existing
   *     "Alfie Insights (Beta)" pattern does not match this wording, so an
   *     entire AI analysis was landing inside the action list.
   *   "Timeline for additional findings" — introduces that same block.
   *   "Recommended Actions" — advisory prose aimed at the customer, distinct
   *     from "SOC Recommended Actions" and from actions the SOC performed.
   */
  { title: 'Alfie Insights', pattern: /Alfie AI Summarization\s*\(Beta\)/ },
  { title: 'Alfie Insights', pattern: /Timeline for additional findings:?/ },
  { title: 'Recommended Actions', pattern: /Recommended Actions:?/ },
];

// Known key names that appear in "Key Details" / "Threat Details" sections.
export const KNOWN_KEYS: string[] = [
  'Alert Snapshot',
  'Threat Classification',
  'Threat Name',
  'Detection Engine',
  'Endpoint Name',
  'Endpoint IP Address',
  'File Path',
  'Site Name',
  'File Hash',
  'File SHA1 Hash',
  'Group Name',
  'File Publisher Name',
  'File Publisher Signed & Verified',
  'SentinelOne Mitigation Policy',
  'SentinelOne Mitigation Status',
  'Command Line',
  'Threat Status',
  'Username',
  'Source IP Address / Hostname',
  'Source IP Address',
  'Source IP Location',
  'Failure Reason (Error Code)',
  'Source OS / Browse',
  'Application',
  'User ID/Display Name',
  'Additional Information',
  'Activity',
  'Client IP',
  'Login Results(s)',
  'App Display Name',
  'IsDevice Compliant',
  'IsDevice Managed',
  'Device Browser / Device Operating System',
  'UserAgent',
  'Source IP',
  'Event',
  'Operation',
  'Threat Name',
  'Threat Status',
  'Time Of Detection',
  'Alert Link',
  'Connector Name',
  'Connector Type',
  'Connector ID',
  'Reason for Disconnect',
  'Tenant',
  'Last Triggered',
  'Meaningful Name',
  'Count of Malicious Engines',
  'Connector Link',
];

interface SectionHit {
  title: string;
  start: number;
  headerEnd: number;
}

interface KeyHit {
  key: string;
  start: number;
  keyEnd: number;
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

export function parseDescription(raw: string): ParsedDescription {
  if (!raw || !raw.trim()) {
    return { summary: '', sections: [] };
  }

  // Find all section header positions.
  const hits: SectionHit[] = [];
  for (const { title, pattern } of SECTION_PATTERNS) {
    const match = pattern.exec(raw);
    if (match) {
      hits.push({ title, start: match.index, headerEnd: match.index + match[0].length });
    }
  }

  // Sort by position in the text.
  hits.sort((a, b) => a.start - b.start);

  // Remove overlapping hits (keep the first one found at each position).
  const deduped: SectionHit[] = [];
  for (const h of hits) {
    if (deduped.length === 0 || h.start >= deduped[deduped.length - 1].headerEnd) {
      deduped.push(h);
    }
  }

  // Extract summary (text before first section).
  const summaryEnd = deduped.length > 0 ? deduped[0].start : raw.length;
  const summary = raw.substring(0, summaryEnd).trim();

  // Extract each section's content.
  const sections: DescriptionSection[] = [];
  for (let i = 0; i < deduped.length; i++) {
    const h = deduped[i];
    const contentStart = h.headerEnd;
    const contentEnd = i + 1 < deduped.length ? deduped[i + 1].start : raw.length;
    const content = raw.substring(contentStart, contentEnd).trim();

    // Skip mostly-empty sections and internal automation sections.
    if (content.length < 5) continue;
    if (isInternalSection(h.title, content)) continue;

    // Parse key-value pairs for detail sections.
    const keyValues = isDetailSection(h.title) ? parseKeyValues(content) : [];

    // Parse action items for remediation / response sections.
    const actionItems = isActionSection(h.title) ? parseActionItems(content) : [];

    sections.push({ title: h.title, content, keyValues, actionItems });
  }

  return { summary, sections };
}

// ---------------------------------------------------------------------------
// Section type checks
// ---------------------------------------------------------------------------

function isDetailSection(title: string): boolean {
  return title === 'Key Details' || title === 'Threat Details' || title === 'Endpoint Details';
}

function isActionSection(title: string): boolean {
  return (
    title === 'Recommended Remediation' ||
    title === 'Remediation Actions' ||
    title === 'SOC Recommended Actions'
  );
}

function isInternalSection(title: string, content: string): boolean {
  if (title === 'Alfie Insights') return true;
  if (title === 'Cross-Service Intelligence') {
    if (content.includes('No relevant tools configured') || content.includes('N/A')) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Key-value extraction
// ---------------------------------------------------------------------------

function parseKeyValues(content: string): { key: string; value: string }[] {
  const results: { key: string; value: string }[] = [];

  // Build a list of (key, position) for keys found in the content.
  const found: KeyHit[] = [];
  for (const key of KNOWN_KEYS) {
    let idx = content.indexOf(`${key}:`);
    if (idx === -1) idx = content.indexOf(key);
    if (idx !== -1) {
      const keyEnd = idx + key.length;
      // Skip past any trailing colon/space.
      let valueStart = keyEnd;
      while (valueStart < content.length && (content[valueStart] === ':' || content[valueStart] === ' ')) {
        valueStart++;
      }
      found.push({ key, start: idx, keyEnd: valueStart });
    }
  }

  if (found.length === 0) return results;

  // Sort by position.
  found.sort((a, b) => a.start - b.start);

  // Remove overlapping keys (keep the longer key match).
  const dedupedKeys: KeyHit[] = [];
  for (const f of found) {
    if (dedupedKeys.length > 0 && f.start < dedupedKeys[dedupedKeys.length - 1].keyEnd) {
      if (f.keyEnd > dedupedKeys[dedupedKeys.length - 1].keyEnd) {
        dedupedKeys[dedupedKeys.length - 1] = f;
      }
      continue;
    }
    dedupedKeys.push(f);
  }

  // Extract values (text between this key's end and the next key's start).
  for (let i = 0; i < dedupedKeys.length; i++) {
    const f = dedupedKeys[i];
    const valueEnd = i + 1 < dedupedKeys.length ? dedupedKeys[i + 1].start : content.length;
    const value = content.substring(f.keyEnd, valueEnd).trim();
    if (value) {
      results.push({ key: f.key, value });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Action item extraction
// ---------------------------------------------------------------------------

function parseActionItems(content: string): { text: string; status: string | null }[] {
  const results: { text: string; status: string | null }[] = [];

  // Match bracketed action tags: [Containment], [Eradication], [Hardening], etc.
  const bracketPattern = /\[([A-Za-z\s]+)\]\s*(.+?)(?=\[|$)/g;
  let match: RegExpExecArray | null;

  const bracketMatches: RegExpExecArray[] = [];
  while ((match = bracketPattern.exec(content)) !== null) {
    bracketMatches.push(match);
  }

  if (bracketMatches.length > 0) {
    for (const m of bracketMatches) {
      const tag = m[1]?.trim() ?? '';
      const text = m[2]?.trim() ?? '';
      if (text) {
        results.push({ text: `[${tag}] ${text}`, status: null });
      }
    }
    return results;
  }

  // Fallback: split on sentence-like boundaries.
  const lines = content
    .split(/(?<=\.)\s+/)
    .filter((l) => l.trim().length > 10);
  for (const line of lines) {
    results.push({ text: line.trim(), status: null });
  }

  return results;
}
